'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { LanguageSelector } from './LanguageSelector';
import { OllsoftLogo } from './OllsoftLogo';
import { useScroll } from '../context/ScrollContext';

// Inline icons. FontAwesome's barrel import (1.14 MB decoded chunk for 2 icons)
// was the single largest JS payload on the home page; framer-motion (829 KB)
// was 2nd largest just for the mobile menu height transition. Both replaced
// here — runtime trace 2026-05-20 showed click handler processingMs of
// 600-1040ms on 20× CPU, dominated by AnimatePresence orchestration.
const HamburgerIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden="true">
    <path d="M3 7h18M3 12h18M3 17h18" />
  </svg>
);
const CloseIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrolled, onDarkBg } = useScroll();
  const locale = useLocale();
  const t = useTranslations('nav');
  const tCommon = useTranslations('common');

  const navItems = [
    { key: 'work',      href: `/${locale}/work`      },
    { key: 'aimodels',  href: `/${locale}/ai-models` },
    { key: 'about',     href: `/${locale}/about`     },
    { key: 'resources', href: `/${locale}/articles`  },
    { key: 'contact',   href: `/${locale}/contact`   },
  ] as const;

  // "Leistungen" is a dropdown only — no own overview page.
  const leistungenItems = [
    { key: 'wissensWorkshop',    href: `/${locale}/leistungen/ki-wissen-workshop`  },
    { key: 'analyseWorkshop',    href: `/${locale}/leistungen/analyse-workshop`    },
    { key: 'individualsoftware', href: `/${locale}/leistungen/individualsoftware`  },
  ] as const;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow,opacity] duration-300 ${
      !onDarkBg
        ? 'bg-white/95 border-b border-black/[0.06] shadow-[0_1px_12px_0_rgba(0,0,0,0.09)]'
        : (scrolled || mobileMenuOpen) ? 'bg-[#030b24]/85' : 'bg-transparent'
    }`}>
      <nav className="nav-gutter">
        <div className="flex justify-between items-center h-[var(--nav-height)]">

          {/* Logo */}
          <a href={`/${locale}`} className="flex-shrink-0" aria-label={tCommon('brand')}>
            <OllsoftLogo variant={onDarkBg ? 'light' : 'dark'} className="h-12 w-auto" />
          </a>

          {/* Desktop: nav + separator + lang + cta */}
          <div className="hidden nav-break:flex items-center gap-8">

            {/* Nav links */}
            <div className="flex items-center gap-[var(--nav-link-gap)]">

              {/* Leistungen dropdown (hover/focus, CSS-only — left of Fallstudien) */}
              <div className="relative group">
                <a
                  href={`/${locale}/leistungen`}
                  aria-haspopup="true"
                  className={`flex items-center gap-1 text-nav tracking-[var(--nav-link-tracking)] whitespace-nowrap transition-all duration-300 group-hover:opacity-60 ${
                    onDarkBg ? 'text-font-primary' : 'text-font-secondary'
                  }`}
                >
                  {t('leistungen')}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/icons/arrow-down.svg"
                    alt=""
                    className={`size-3.5 ${onDarkBg ? 'invert' : ''} transition-transform duration-300 group-hover:rotate-180`}
                  />
                </a>
                <div className="absolute left-0 top-full pt-4 hidden group-hover:block group-focus-within:block">
                  <div className={`w-56 rounded-[var(--border-radius-primary)] overflow-hidden py-1.5 ${
                    onDarkBg
                      ? 'bg-[#030b24]/90 backdrop-blur-md border border-white/10'
                      : 'bg-white/95 backdrop-blur-md border border-black/[0.08] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)]'
                  }`}>
                    {leistungenItems.map((item) => (
                      <a
                        key={item.key}
                        href={item.href}
                        className={`block px-4 py-2.5 text-nav tracking-[var(--nav-link-tracking)] transition-colors ${
                          onDarkBg ? 'text-font-primary hover:bg-white/5' : 'text-font-secondary hover:bg-black/[0.03]'
                        }`}
                      >
                        {t(item.key)}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  className={`text-nav tracking-[var(--nav-link-tracking)] whitespace-nowrap hover:opacity-60 transition-all duration-300 ${
                    onDarkBg ? 'text-font-primary' : 'text-font-secondary'
                  }`}
                >
                  {t(item.key)}
                </a>
              ))}
            </div>

            {/* Vertical separator */}
            <div className={`h-[var(--nav-separator-h)] w-px transition-all duration-300 ${onDarkBg ? 'bg-background-primary opacity-25' : 'bg-font-secondary opacity-15'}`} />

            {/* Language selector + CTA */}
            <div className="flex items-center gap-[var(--nav-right-gap)]">
              <LanguageSelector dark={onDarkBg} />
              <a
                href={`/${locale}/contact`}
                className={`border-1 border-border-secondary/15 
                rounded-[var(--border-radius-primary)] tracking-[var(--nav-cta-tracking)] font-medium whitespace-nowrap 
                px-[var(--nav-cta-px)] py-[var(--nav-cta-py)] transition-colors leading-none 
                ${onDarkBg ? 'text-font-secondary bg-background-primary hover:bg-background-primary/90' : 'text-font-primary bg-background-secondary hover:bg-background-secondary/80'}
                }`}
              >
                {t('termin')}
              </a>
            </div>
          </div>

          {/* Mobile: hamburger */}
          <button
            className={`nav-break:hidden p-2 transition-colors duration-300 ${onDarkBg ? 'text-font-primary' : 'text-font-secondary'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen
              ? <CloseIcon className="w-6 h-6" />
              : <HamburgerIcon className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile nav — pure CSS grid-template-rows transition (no JS animation
            library). Closed: grid-rows-[0fr] collapses the track to 0 and the
            inner overflow:hidden clips content. Open: grid-rows-[1fr] expands
            to natural height. GPU-cheap, click handler stays under 50ms. */}
        <div
          id="mobile-nav"
          aria-hidden={!mobileMenuOpen}
          className={`nav-break:hidden grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out ${
            mobileMenuOpen
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0 pointer-events-none'
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-5 py-5 border-t border-border-primary/10">

              {/* Leistungen group */}
              <div className="flex flex-col gap-3">
                <a
                  href={`/${locale}/leistungen`}
                  className={`text-base font-medium ${onDarkBg ? 'text-font-primary' : 'text-font-secondary'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  tabIndex={mobileMenuOpen ? 0 : -1}
                >
                  {t('leistungen')}
                </a>
                {leistungenItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    className={`pl-4 text-base transition-colors ${onDarkBg ? 'text-font-primary/80 hover:text-font-primary' : 'text-font-secondary/80 hover:text-font-secondary'}`}
                    onClick={() => setMobileMenuOpen(false)}
                    tabIndex={mobileMenuOpen ? 0 : -1}
                  >
                    {t(item.key)}
                  </a>
                ))}
              </div>

              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  className={`hover:text-font-primary text-base transition-colors ${onDarkBg ? 'text-font-primary' : 'text-font-secondary'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  tabIndex={mobileMenuOpen ? 0 : -1}
                >
                  {t(item.key)}
                </a>
              ))}
              <LanguageSelector dark={onDarkBg} />
              <a
                href={`/${locale}/contact`}
                className={`px-6 py-3.5 rounded-[var(--border-radius-primary)] text-base font-medium text-center ${onDarkBg ? 'text-font-secondary bg-background-primary' : 'text-font-primary bg-background-secondary'}`}
                onClick={() => setMobileMenuOpen(false)}
                tabIndex={mobileMenuOpen ? 0 : -1}
              >
                {t('termin')}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
